import { useState } from 'react';
import axios from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import projects from '../Projects/projects';

const CodeBlock = ({ className, children, ...props }) => {
  const [copied, setCopied] = useState(false);
  const codeString = String(children).replace(/\n$/, "");
  const match = /language-(\w+)/.exec(className || "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className="relative group my-4 rounded-lg overflow-hidden border border-slate-700 bg-slate-950 text-sm">
      <div className="flex justify-between items-center px-4 py-1.5 bg-slate-800 text-xs text-slate-400 font-mono select-none border-b border-slate-700">
        <span>{match ? match[1] : "code"}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-cyan-400 hover:text-cyan-300 font-sans transition py-0.5 px-2 rounded hover:bg-slate-700/50"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto font-mono text-slate-200 leading-relaxed bg-transparent">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
};

const CASE_STUDY_TEMPLATE = `## 📌 Executive Summary
*Provide a high-level 1-2 sentence overview of the project, problem, and results achieved.*

## 🚨 The Challenge & Context
*Explain the business problem, user needs, performance bottleneck, or technical gap that you set out to solve.*

## ⚙️ Architecture & Strategic Design
*Detail your strategy, tech stack selections, database schemas, or service integrations. (Feel free to use flowcharts/Mermaid here).*

## 🛠️ Technical Implementation
*Deep-dive into specific code snippets, algorithms, optimizations, or configurations you built.*

\`\`\`javascript
// Example code snippet
const optimize = (data) => {
  return data.map(item => ({ ...item, processed: true }));
};
\`\`\`

## 📈 Results & Key Metrics
*Share the tangible outcomes of your work (e.g. "Reduced page load time by 35%", "Achieved 99.9% uptime", "Automated 20hrs/week of manual syncs").*

## 🧠 Retrospective & Learnings
*Discuss the trade-offs, what went well, and what you would design differently next time.*`;

const CreateBlog = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General Tech');
  const [projectSlug, setProjectSlug] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('write');

  const navigate = useNavigate();

  const handleInsertTemplate = () => {
    setContent(CASE_STUDY_TEMPLATE);
  };

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handlePostBlog = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim() || !content.trim()) {
      setError('Title and content are required.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        '/blog/',
        { title, content, tags, category, projectSlug },
        { withCredentials: true }
      );

      if (response.status === 201 || response.status === 200) {
        setSuccess('Blog posted successfully!');
        navigate("/blog");
        setTitle('');
        setContent('');
        setTags([]);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        setError('You need to log in first to post a blog.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    }

    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto mt-20 p-4 sm:p-8 bg-[#151515] text-white rounded-2xl shadow-xl border border-white/10">
      <h1 className="text-3xl font-bold mb-2 text-cyan-400">Create a New Blog Post</h1>
      <p className="text-base text-gray-300 mb-6">Share your knowledge and ideas with the community.</p>

      {error && <div className="text-red-400 font-semibold mb-4">{error}</div>}
      {success && <div className="text-green-400 font-semibold mb-4">{success}</div>}

      <form onSubmit={handlePostBlog} className="flex flex-col gap-5">
        <input
          className="px-4 py-3 bg-black/30 border border-cyan-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          placeholder="Blog Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* Category & Project Link Toggles */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-gray-300">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-3 bg-black/30 border border-cyan-600 rounded-md text-white w-full focus:outline-none focus:ring-2 focus:ring-cyan-500 mt-1"
            >
              <option value="General Tech" className="bg-slate-900 text-white">General Tech</option>
              <option value="Case Study" className="bg-slate-900 text-white">Case Study</option>
              <option value="Tech Journey" className="bg-slate-900 text-white">Tech Journey</option>
              <option value="Project Deep Dive" className="bg-slate-900 text-white">Project Deep Dive</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-300">Link to Project (Optional)</label>
            <select
              value={projectSlug}
              onChange={(e) => setProjectSlug(e.target.value)}
              className="px-4 py-3 bg-black/30 border border-cyan-600 rounded-md text-white w-full focus:outline-none focus:ring-2 focus:ring-cyan-500 mt-1"
            >
              <option value="" className="bg-slate-900 text-white">-- No Linked Project --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id} className="bg-slate-900 text-white">
                  {p.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content Tabs & Injector */}
        <div className="flex flex-wrap justify-between items-center gap-3 border-b border-white/10 pb-2 mt-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('write')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === 'write'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              Write
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === 'preview'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              Preview
            </button>
          </div>

          <button
            type="button"
            onClick={handleInsertTemplate}
            className="text-xs px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-cyan-400 hover:bg-slate-700 transition"
          >
            💡 Insert Case Study Template
          </button>
        </div>

        {activeTab === 'write' ? (
          <textarea
            className="px-4 py-3 bg-black/30 border border-cyan-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 h-64 resize-vertical font-mono text-sm"
            placeholder="Start writing your story in Markdown..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        ) : (
          <div className="prose prose-invert max-w-none bg-slate-900/30 border border-slate-700 rounded-md p-4 min-h-[16rem] overflow-y-auto">
            {content.trim() ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ className, children, ...props }) {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-slate-800 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-sm" {...props}>
                        {children}
                      </code>
                    ) : (
                      <CodeBlock className={className} {...props}>
                        {children}
                      </CodeBlock>
                    );
                  },
                }}
              >
                {content}
              </ReactMarkdown>
            ) : (
              <p className="text-gray-500 italic text-sm">Nothing to preview yet. Start writing in the 'Write' tab.</p>
            )}
          </div>
        )}

        {/* Tag Input */}
        <div>
          <label className="text-sm font-semibold">Tags (press Enter or click Add)</label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Enter tag"
              className="px-3 py-2 border rounded-md text-sm bg-transparent text-white border-cyan-600 w-full"
            />
            <button type="button" onClick={addTag} className="text-sm px-3 py-2 bg-cyan-600 text-white rounded-md">
              Add
            </button>
          </div>
          {/* Display Tags */}
          <div className="flex gap-2 flex-wrap mt-2">
            {tags.map((tag, idx) => (
              <span key={idx} className="bg-cyan-700 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                {tag}
                <button type="button" onClick={() => removeTag(tag)} className="text-red-400">×</button>
              </span>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 px-6 rounded-md transition duration-200 disabled:bg-cyan-400"
          disabled={loading}
        >
          {loading ? 'Posting...' : 'Publish Blog'}
        </button>
      </form>
    </div>
  );
};

export default CreateBlog;
